import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { 
  Box, 
  Divider, 
  IconButton, 
  Modal, 
  Typography, 
  useTheme,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Chip,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';

import { OrderOptionsValues, VendorHotel } from '@bandido/shared/interfaces';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { QueryKeys } from '@frontend/features/common/types';

import { useGetHotelVendorHotels } from '../../queries';
import { LoadingSkeleton } from '../Jobs';
import { useLinkHotels } from '../../mutations';
import { useQueryClient } from 'react-query';
import { useCallback } from 'react';

const WideModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: 1200,
  height: '80vh',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));


interface HotelLinkModalProps {
  open: boolean;
  onClose: () => void;
  hotelName: string;
  nimbuxParentId: number;
}

export const HotelLinkModal = ({ open, onClose, hotelName, nimbuxParentId }: HotelLinkModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useDebouncedState('', 500);
  const queryClient = useQueryClient();

  const { data: allVendorHotels, isLoading: isLoadingVendorHotels } = useGetHotelVendorHotels({
    nimbuxParentId: null,
    searchText,
    currentPage: 1,
    pageSize: 20,
    sort: 'id',
    order: OrderOptionsValues.asc,
  });

  const { data: linkedHotels, isLoading: isLoadingLinkedHotels } = useGetHotelVendorHotels({
    nimbuxParentId,
    currentPage: 1,
    pageSize: 50,
    sort: 'id',
    order: OrderOptionsValues.asc,
  });

  const { mutate: linkHotel, isLoading: isLinking } = useLinkHotels({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getHotelVendorHotels]);
    },
  });

  const handleLink = (hotel: VendorHotel) => {
    linkHotel({
      nimbuxParentId: nimbuxParentId,
      vendorHotelId: hotel.id!,
    });
  };

  const handleUnlink = (hotel: VendorHotel) => {
    linkHotel({
      nimbuxParentId: null,
      vendorHotelId: hotel.id!,
    });
  };

  const handleInputChange = useCallback((value: string) => setSearchText(value), [setSearchText]);

  const renderHotelItem = (hotel: VendorHotel, isLinked: boolean) => (
    <ListItem
      key={hotel.id}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        mb: 1,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {hotel.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
          <Typography variant="body2" color="text.secondary">
            {hotel.city}, {hotel.country}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.disabled">
            {t('hotelContent.nimbuxHotels.linkModal.parentHotel')}:
          </Typography>
          { 
            !isLinked && (
              <Chip
                label={hotel.nimbuxParentName || t('hotelContent.nimbuxHotels.linkModal.noParentHotel')}
                size="small"
                variant={hotel.nimbuxParentName ? 'filled' : 'outlined'}
                color={hotel.nimbuxParentName ? 'primary' : 'default'}
              />
            )
          }
          <Chip
            label={hotel.supplierName || t('hotelContent.nimbuxHotels.linkModal.noSupplier')}
            size="small"
            variant={hotel.supplierName ? 'filled' : 'outlined'}
            color={hotel.supplierName ? 'secondary' : 'default'}
          />
        </Box>
      </Box>
      <Box sx={{ ml: 2 }}>
        {isLinked ? (
          <LoadingButton
            disabled={!linkedHotels?.items || linkedHotels.items.length <= 1}
            loading={isLinking}
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleUnlink(hotel)}
          >
            {t('hotelContent.nimbuxHotels.linkModal.unlink')}
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={isLinking}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleLink(hotel)}
          >
            {t('hotelContent.nimbuxHotels.linkModal.link')}
          </LoadingButton>
        )}
      </Box>
    </ListItem>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={t('hotelContent.nimbuxHotels.linkModal.title', { hotelName })}
    >
      <WideModalBox>
        {/* Header */}
        <Box display="flex" gap={theme.spacing(2)} mb={2}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Typography id="hotel-link-modal-title" variant="h6" component="h2">
              {t('hotelContent.nimbuxHotels.linkModal.title', { hotelName })}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ height: 'calc(100% - 80px)', overflow: 'hidden' }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={6} sx={{ height: '100%' }}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  {t('hotelContent.nimbuxHotels.linkModal.linkedHotels')}
                </Typography>
                {isLoadingLinkedHotels ? (
                  <LoadingSkeleton />
                ) : (
                  <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
                    {linkedHotels?.items.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: theme.palette.text.disabled,
                        }}
                      >
                        <Typography variant="body2">
                          {t('hotelContent.nimbuxHotels.linkModal.noLinkedHotels')}
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ padding: 0 }}>
                        {linkedHotels?.items.map(hotel => renderHotelItem(hotel, true))}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={6} sx={{ height: '100%' }}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  {t('hotelContent.nimbuxHotels.linkModal.availableHotels')}
                </Typography>
                
                <TextField
                  size="small"
                  placeholder={t('hotelContent.nimbuxHotels.linkModal.searchPlaceholder')}
                  
                  onChange={(e) => handleInputChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                {isLoadingVendorHotels ? (
                  <LoadingSkeleton />
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Box sx={{ flex: 1, overflow: 'auto', pr: 1, minHeight: 0 }}>
                      {allVendorHotels?.items.length === 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: theme.palette.text.disabled,
                          }}
                        >
                          <Typography variant="body2">
                            {t('hotelContent.nimbuxHotels.linkModal.noAvailableHotels')}
                          </Typography>
                        </Box>
                      ) : (
                        <List sx={{ padding: 0 }}>
                          {allVendorHotels?.items.map(hotel => renderHotelItem(hotel, false))}
                        </List>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </WideModalBox>
    </Modal>
  );
};
